import { locales } from "@/config/locale";

export type DiscoveryFamily =
	| "catalog"
	| "category"
	| "product"
	| "guide"
	| "lexicon"
	| "comparison"
	| "calculator"
	| "half_life_calculator";
type DiscoveryPlacement =
	| "product_card"
	| "category_copy"
	| "comparison_table"
	| "related_resources"
	| "category_navigation"
	| "content";

const SLUG = /^[a-z0-9][a-z0-9-]{0,99}$/;
const PLACEMENTS = new Set<string>([
	"product_card",
	"category_copy",
	"comparison_table",
	"related_resources",
	"category_navigation",
]);

/** Only machine identities from the same locale/channel enter discovery analytics. */
export function getDiscoveryTarget(
	href: string,
	origin: string,
	locale: string,
	channel: string,
): { family: DiscoveryFamily; slug: string } | null {
	if (!(locales as readonly string[]).includes(locale) || !["eu", "us"].includes(channel)) return null;
	try {
		const url = new URL(href, origin);
		if (url.origin !== origin || !["http:", "https:"].includes(url.protocol)) return null;
		const parts = url.pathname.split("/").filter(Boolean);
		if (parts[0] !== locale || parts[1] !== channel) return null;
		if (parts.length === 3 && parts[2] === "products") return { family: "catalog", slug: "products" };
		if (parts.length === 3 && parts[2] === "peptides") return { family: "lexicon", slug: "peptides" };
		if (parts.length === 5 && parts[2] === "peptides" && parts[3] === "compare" && SLUG.test(parts[4]))
			return { family: "comparison", slug: parts[4] };
		if (parts.length !== 4 || !SLUG.test(parts[3])) return null;
		const families: Record<string, DiscoveryFamily> = {
			products: "product",
			categories: "category",
			guides: "guide",
			peptides: "lexicon",
			"peptide-calculator": "calculator",
			"half-life-calculator": "half_life_calculator",
		};
		const family = families[parts[2]];
		return family ? { family, slug: parts[3] } : null;
	} catch {
		return null;
	}
}

export function getDiscoveryProperties(input: {
	sourceFamily: DiscoveryFamily;
	sourceSlug: string;
	href: string;
	origin: string;
	locale: string;
	channel: string;
	placement?: string | null;
}) {
	if (!SLUG.test(input.sourceSlug)) return null;
	const target = getDiscoveryTarget(input.href, input.origin, input.locale, input.channel);
	if (!target || (target.family === input.sourceFamily && target.slug === input.sourceSlug)) return null;
	const placement = PLACEMENTS.has(input.placement ?? "")
		? (input.placement as DiscoveryPlacement)
		: "content";
	return {
		link_id: [
			input.locale,
			input.channel,
			input.sourceFamily,
			input.sourceSlug,
			placement,
			target.family,
			target.slug,
		].join(":"),
		source_family: input.sourceFamily,
		source_slug: input.sourceSlug,
		target_family: target.family,
		target_slug: target.slug,
		placement,
		locale: input.locale,
		channel: input.channel,
	};
}
